import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface ResourcesHubProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ResourcesHub({
  setPage
}: ResourcesHubProps) {
  const articles = [{
    category: 'Guide',
    title: 'Dustless vs. Dry Blasting: Which is Right for Your Project?',
    desc: 'Understand the key differences, benefits, and ideal applications for wet and dry abrasive blasting methods.',
    date: 'October 12, 2023'
  }, {
    category: 'Pricing',
    title: 'How Much Does Mobile Blasting Cost in Michigan?',
    desc: 'A breakdown of how we price projects, from hourly rates to flat project fees, and what factors impact your estimate.',
    date: 'September 28, 2023'
  }, {
    category: 'Automotive',
    title: 'Preparing Your Classic Car for Media Blasting',
    desc: 'Steps you should take before our mobile crew arrives to ensure the best possible results for your restoration.',
    date: 'August 15, 2023'
  }, {
    category: 'Compliance',
    title: 'EPA and DEQ Considerations for Industrial Surface Prep',
    desc: 'Why dustless blasting is often the preferred choice for projects requiring strict environmental compliance.',
    date: 'July 22, 2023'
  }, {
    category: 'Marine',
    title: 'The Safest Way to Remove Bottom Paint from Fiberglass',
    desc: 'Learn how adjustable pressure and water suppression protect your gelcoat during the stripping process.',
    date: 'June 10, 2023'
  }, {
    category: 'FAQ',
    title: 'What Media Do You Use for Blasting?',
    desc: 'An overview of the different abrasives we use, from crushed glass to walnut shells, and when we use them.',
    date: 'May 05, 2023'
  }];
  return <div className="w-full bg-white" data-id="element-524">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-525">
        <div className="max-w-7xl mx-auto text-center" data-id="element-526">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-6" data-id="element-527">
            Resources & <span className="text-brand-red" data-id="element-528">Guides</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg md:text-xl text-steel-300 font-body max-w-3xl mx-auto leading-relaxed" data-id="element-529">
            Expert advice, pricing information, and surface preparation guides
            from Michigan's trusted mobile blasting professionals.
          </motion.p>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-12 bg-white border-b border-steel-100" data-id="element-530">
        <div className="max-w-7xl mx-auto px-4" data-id="element-531">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="bg-steel-50 rounded-sm overflow-hidden border border-steel-200 flex flex-col md:flex-row" data-id="element-532">
            <div className="md:w-1/2 h-64 md:h-auto relative" data-id="element-533">
              <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000&auto=format&fit=crop" alt="Industrial worker" className="w-full h-full object-cover" data-id="element-534" />
              <div className="absolute top-4 left-4 bg-brand-red text-white text-xs font-bold uppercase px-3 py-1 tracking-wider" data-id="element-535">
                Featured
              </div>
            </div>
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center" data-id="element-536">
              <div className="flex items-center text-steel-500 text-sm mb-4 font-semibold uppercase tracking-wider" data-id="element-537">
                <BookOpenIcon className="w-4 h-4 mr-2" data-id="element-538" />
                Comprehensive Guide
              </div>
              <h2 className="text-2xl md:text-4xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-539">
                The Complete Guide to Industrial Surface Preparation
              </h2>
              <p className="text-steel-600 mb-8 leading-relaxed" data-id="element-540">
                Everything facility managers and contractors need to know about
                choosing the right blasting method, managing containment, and
                ensuring proper coating adhesion.
              </p>
              <Button variant="primary" className="self-start" data-id="element-541">
                Read Full Guide
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 bg-white" data-id="element-542">
        <div className="max-w-7xl mx-auto px-4" data-id="element-543">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-544">
            {articles.map((article, idx) => <motion.article key={idx} variants={fadeInUp} className="border border-steel-200 p-8 hover:border-brand-red hover:shadow-lg transition-all duration-300 flex flex-col h-full group cursor-pointer" data-id="element-545">
                <div className="text-brand-red text-xs font-bold uppercase tracking-wider mb-3" data-id="element-546">
                  {article.category}
                </div>
                <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3 group-hover:text-brand-red transition-colors" data-id="element-547">
                  {article.title}
                </h3>
                <p className="text-steel-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-548">
                  {article.desc}
                </p>
                <div className="flex items-center justify-between mt-auto pt-6 border-t border-steel-100" data-id="element-549">
                  <span className="text-steel-400 text-xs" data-id="element-550">{article.date}</span>
                  <ArrowRightIcon className="w-5 h-5 text-steel-300 group-hover:text-brand-red group-hover:translate-x-1 transition-all" data-id="element-551" />
                </div>
              </motion.article>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-steel-50 text-center px-4" data-id="element-552">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-2xl mx-auto" data-id="element-553">
          <h2 className="text-3xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-554">
            Have a Specific Question?
          </h2>
          <p className="text-steel-600 mb-8" data-id="element-555">
            We're happy to discuss your project details and provide expert
            recommendations.
          </p>
          <Button variant="primary" onClick={() => setPage('contact')} data-id="element-556">
            Contact Our Team
          </Button>
        </motion.div>
      </section>
    </div>;
}